/**
 * issue #76 Phase 2 - singleton polling worker (plan §5.2.E)。
 *
 * 既存 `useJobStatusPolling` の poll ロジックをモジュールレベルに移植し、
 * 並列複数 jobId を 1 つのワーカーで駆動する。 進捗は localStorage と
 * subscribe コールバックの双方に書き戻す。
 *
 * - `fingeringApi.getJobStatus` で 1 件取得 → markJobFinished で完了書き戻し
 * - showToast / triggerDriveSave は {@link setCallbacks} で DI
 * - module 初回ロード時に 1 度だけ `document.visibilitychange` を仕掛け、
 *   visible 復帰で active な全 jobId を即 re-poll する
 *
 * テストは {@link __resetForTests} で state を破棄する。 production 側は
 * {@link bootstrap} を 1 回呼ぶだけで `listActiveJobs()` の全 entry を引き継ぐ。
 */
import { fingeringApi } from './fingeringApi';
import type { JobStatusValue } from './fingeringApi';
import {
  listActiveJobs,
  loadJobLifecycleEntry,
  clearJobLifecycleEntry,
  markJobFinished,
  markJobStatus,
  EXPIRED_AUTOSAVED_ERROR_KIND,
} from '../utils/jobLifecycleStorage';

/**
 * issue #95 (#28): 自動保存 ON の expired 確定時に出す「Drive 保存済みの可能性・要確認」誘導文言。
 * 失敗扱いせず、 履歴を確認すれば結果が見つかる可能性を伝える。
 */
const EXPIRED_AUTOSAVED_MESSAGE =
  '処理は完了し Drive に保存された可能性があります。 履歴を確認してください';

/** poll の標準間隔 (ms)。 既存 useJobStatusPolling.DEFAULT_POLL_INTERVAL_MS と同期。 */
export const POLL_INTERVAL_MS = 2000;

/** ネットワーク例外の retry budget。 既存 MAX_RETRY_COUNT と同期。 */
export const MAX_RETRY_COUNT = 5;

/** 404→expired の確定までの猶予回数。 既存 MAX_EXPIRED_RETRY_COUNT と同期。 */
export const MAX_EXPIRED_RETRY_COUNT = 5;

const BACKOFF_MULTIPLIER = 2;

const SUCCESS_STATUSES: ReadonlySet<JobStatusValue> = new Set<JobStatusValue>(['done']);
const FAILURE_STATUSES: ReadonlySet<JobStatusValue> = new Set<JobStatusValue>([
  'failed',
  'error_processing',
]);

export interface TrackerCallbacks {
  /** トースト表示 (success / error / info 等)。 Phase 1 で導入された ToastProvider を DI 経由で結線する。 */
  showToast?: (kind: 'success' | 'error' | 'info', message: string) => void;
  /** done 時に Drive 自動保存を発火する。 fire-and-forget で構わない。 */
  triggerDriveSave?: (jobId: string) => void | Promise<void>;
  /** issue #85: done 検知を React 層へ通知する。 IndexedDB 上書き判断は React 層が握る。 */
  onJobDone?: (jobId: string) => void;
  /** issue #116 FE-1: 失敗確定 (error toast を出す経路) を React 層へ通知する。 ErrorModal トリガ。 */
  onJobError?: (jobId: string) => void;
}

export interface RegisterJobOpts {
  /** watchdog (issue #36 + #72): epoch ms。 経過後の最終 polling 1 回で救済 or 確定。 */
  deadlineAt?: number;
}

export interface TrackerState {
  /** 現在 active な jobId 一覧 (新しい register 順)。 */
  activeJobIds: ReadonlyArray<string>;
}

export type TrackerListener = (state: TrackerState) => void;

interface PollState {
  jobId: string;
  timerId: ReturnType<typeof setTimeout> | null;
  retryCount: number;
  expiredCount: number;
  deadlineAt?: number;
  settled: boolean;
}

const states = new Map<string, PollState>();
const listeners = new Set<TrackerListener>();
let callbacks: TrackerCallbacks = {};
let visibilityHandlerAttached = false;

function snapshot(): TrackerState {
  return { activeJobIds: Array.from(states.keys()) };
}

function broadcast(): void {
  const state = snapshot();
  listeners.forEach((listener) => {
    try {
      listener(state);
    } catch {
      // listener 例外で他購読者を巻き込まない
    }
  });
}

function ensureVisibilityListener(): void {
  if (visibilityHandlerAttached) return;
  if (typeof document === 'undefined') return;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    // active 全 jobId を即 re-poll する。 backoff timer が hidden 中に止まっていた状態を救済。
    states.forEach((state) => {
      if (state.settled) return;
      if (state.timerId !== null) {
        clearTimeout(state.timerId);
        state.timerId = null;
      }
      void poll(state.jobId);
    });
  });
  visibilityHandlerAttached = true;
}

function settleAndDelete(jobId: string): void {
  const state = states.get(jobId);
  if (state) {
    state.settled = true;
    if (state.timerId !== null) {
      clearTimeout(state.timerId);
      state.timerId = null;
    }
  }
  states.delete(jobId);
  broadcast();
}

/**
 * 失敗確定 (showToast('error') を出す 4 経路: FAILURE_STATUSES / watchdog 最終失敗 /
 * watchdog timeout / ネットワーク例外 budget) の共通後始末。
 * markJobFinished → error toast → onJobError 通知 (例外は握り polling を壊さない) → settle の
 * 発火順序をひとまとめにする (issue #116 FE-1 の 4 サイト重複を集約)。
 */
function finalizeFailure(
  jobId: string,
  status: JobStatusValue,
  errorKind: string,
  message: string,
): void {
  markJobFinished(jobId, status, { errorKind, errorMessage: message });
  callbacks.showToast?.('error', message);
  try {
    callbacks.onJobError?.(jobId);
  } catch {
    // 通知失敗で polling を壊さない
  }
  settleAndDelete(jobId);
}

async function poll(jobId: string): Promise<void> {
  const state = states.get(jobId);
  if (!state || state.settled) return;

  try {
    const res = await fingeringApi.getJobStatus(jobId);
    if (!states.has(jobId)) return; // unregister された
    state.retryCount = 0;

    if (SUCCESS_STATUSES.has(res.status)) {
      // issue #76 Phase 4-β: 画面非表示中に done になった場合は notifyOnReturn=true で
      // 画面復帰時に CompletionBanner で通知する。
      // issue #85: done entry は visible でも残す (自動復元が entry.params.mode/fileName を読むため)。
      // 復元成功後に FingeringPage 側が clearJobLifecycleEntry する。
      const wasHidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
      markJobFinished(jobId, 'done', { notifyOnReturn: wasHidden });
      try {
        callbacks.onJobDone?.(jobId);
      } catch {
        // 通知失敗で polling を壊さない
      }
      try {
        void callbacks.triggerDriveSave?.(jobId);
      } catch {
        // fire-and-forget。 例外で polling 自体を壊さない
      }
      settleAndDelete(jobId);
      return;
    }

    if (res.status === 'expired') {
      state.expiredCount += 1;
      if (state.expiredCount < MAX_EXPIRED_RETRY_COUNT) {
        state.timerId = setTimeout(() => void poll(jobId), POLL_INTERVAL_MS * BACKOFF_MULTIPLIER);
        return;
      }
      // expired 確定時の後始末を driveSaveTarget の有無で出し分ける。
      // budget 5 回消化後の確定 expired のみが対象なので、 cold start 直後の transient な
      // expired は救済済み (上の budget 未満 return)。
      // - driveSaveTarget なし (OFF / 再生成経路の幽霊 entry、 issue #93/#29): server が知らない
      //   (復元不能) 上にユーザは Drive 保存も追跡も意図していない。 「直近失敗」履歴を汚さず clear
      //   (結果は手元の IndexedDB に残っている可能性がある)。
      // - driveSaveTarget あり (ON 経路、 issue #95/#28): 自動保存が有効なので backend が job 完了時に
      //   Drive へ保存済みの可能性が高い (production の in-memory job store 消失で expired 化しても
      //   実際は成功)。 'failed' 扱いにせず errorKind='expired_autosaved' でマークし、 履歴 UI では
      //   赤の「直近失敗」でなく中立の「要確認」+ Drive 履歴誘導に倒す。
      const entry = loadJobLifecycleEntry(jobId);
      if (entry?.driveSaveTarget === undefined) {
        clearJobLifecycleEntry(jobId);
      } else {
        markJobFinished(jobId, 'expired', {
          errorKind: EXPIRED_AUTOSAVED_ERROR_KIND,
          errorMessage: EXPIRED_AUTOSAVED_MESSAGE,
        });
      }
      settleAndDelete(jobId);
      return;
    }

    state.expiredCount = 0;

    if (FAILURE_STATUSES.has(res.status)) {
      const message = res.error ?? 'ジョブの処理に失敗しました';
      // issue #76 Phase 5-α: backend res.errorKind (mxl_missing / omr_timeout 等) を保持。
      // 未指定なら status から fallback (旧挙動の維持)。
      const resolvedErrorKind =
        res.errorKind ?? (res.status === 'error_processing' ? 'error_processing' : 'failed');
      finalizeFailure(jobId, res.status, resolvedErrorKind, message);
      return;
    }

    // watchdog: deadline 経過後の最終 polling 1 回で救済 or 確定
    if (state.deadlineAt !== undefined && Date.now() >= state.deadlineAt) {
      let finalRes: Awaited<ReturnType<typeof fingeringApi.getJobStatus>> | null = null;
      try {
        finalRes = await fingeringApi.getJobStatus(jobId);
      } catch {
        // 例外は救済不能扱い (watchdog 確定に倒す)
      }
      if (!states.has(jobId)) return;

      if (finalRes && SUCCESS_STATUSES.has(finalRes.status)) {
        markJobFinished(jobId, 'done');
        // issue #85: clear せず onJobDone 通知 (上記 done 分岐と同方針)。
        try {
          callbacks.onJobDone?.(jobId);
        } catch {
          // ignore
        }
        try {
          void callbacks.triggerDriveSave?.(jobId);
        } catch {
          // ignore
        }
        settleAndDelete(jobId);
        return;
      }
      if (
        finalRes &&
        (finalRes.status === 'failed' || finalRes.status === 'error_processing')
      ) {
        const message = finalRes.error ?? 'ジョブの処理に失敗しました';
        // issue #76 Phase 5-α: watchdog 救済後も backend finalRes.errorKind を保持する。
        const resolvedErrorKind =
          finalRes.errorKind ??
          (finalRes.status === 'error_processing' ? 'error_processing' : 'failed');
        finalizeFailure(jobId, finalRes.status, resolvedErrorKind, message);
        return;
      }
      // 依然 processing/queued or 取得不能 → watchdog 確定
      const message = '処理が制限時間 (30 分) を超えました。 もう一度お試しください';
      finalizeFailure(jobId, 'failed', 'watchdog', message);
      return;
    }

    // issue #88: processing / queued の中間遷移を localStorage entry.status へ書き戻す。
    // 終端 (done/expired/failure/watchdog) は上で return 済みなので、 ここに来るのは
    // processing か queued のみ。 変化があったときだけ broadcast し、 履歴一覧の
    // 「順番待ち / 処理中」表示を Context 再 render で追従させる (無変化なら無駄 broadcast しない)。
    if (markJobStatus(jobId, res.status)) {
      broadcast();
    }

    // processing / queued: 次回 poll を予約
    state.timerId = setTimeout(() => void poll(jobId), POLL_INTERVAL_MS);
  } catch (err) {
    if (!states.has(jobId)) return;
    if (state.retryCount < MAX_RETRY_COUNT) {
      state.retryCount += 1;
      state.timerId = setTimeout(
        () => void poll(jobId),
        POLL_INTERVAL_MS * BACKOFF_MULTIPLIER,
      );
      return;
    }
    const message = err instanceof Error ? err.message : 'ジョブ状態の取得に失敗しました';
    finalizeFailure(jobId, 'failed', 'failed', message);
  }
}

/** showToast / triggerDriveSave を DI する。 後から差し替え可能。 */
export function setCallbacks(cb: TrackerCallbacks): void {
  callbacks = { ...cb };
}

/** 既存 jobId は no-op。 新規なら PollState を作って即 poll を起動する。 */
export function registerJob(jobId: string, opts: RegisterJobOpts = {}): void {
  if (states.has(jobId)) return;
  states.set(jobId, {
    jobId,
    timerId: null,
    retryCount: 0,
    expiredCount: 0,
    deadlineAt: opts.deadlineAt,
    settled: false,
  });
  ensureVisibilityListener();
  broadcast();
  void poll(jobId);
}

/** Map から削除して進行中 timer を止める。 不在は no-op。 */
export function unregisterJob(jobId: string): void {
  const state = states.get(jobId);
  if (!state) return;
  state.settled = true;
  if (state.timerId !== null) {
    clearTimeout(state.timerId);
    state.timerId = null;
  }
  states.delete(jobId);
  broadcast();
}

/**
 * issue #110: 履歴 UI で失敗 entry をユーザが手動で消す。 storage を clear するだけでは
 * JobTracker の snapshot (recentlyFailedJobs) が更新されず UI に残り「消す」が無反応になるため、
 * clear 後に broadcast して subscriber を再描画する (registerJob / unregisterJob と同じ結線)。
 */
export function dismissFailedJob(jobId: string): void {
  clearJobLifecycleEntry(jobId);
  broadcast();
}

/** jobId が tracker の管理下にあるかを返す。 useJobLifecycleResume の二重 resume ガード用。 */
export function isTracking(jobId: string): boolean {
  return states.has(jobId);
}

/** state 変化通知を購読する。 戻り値で unsubscribe。 */
export function subscribe(listener: TrackerListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** `listActiveJobs()` の全 entry を register する。 App 起動時 1 回呼ぶ想定。 */
export function bootstrap(): void {
  listActiveJobs().forEach((entry) => {
    registerJob(entry.jobId);
  });
}

/** テスト専用: 全 state / listeners / callbacks をリセット。 */
export function __resetForTests(): void {
  states.forEach((state) => {
    if (state.timerId !== null) clearTimeout(state.timerId);
  });
  states.clear();
  listeners.clear();
  callbacks = {};
}
